import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFeatures } from '../contexts/FeatureContext';
import { usePhoneLandscape } from '../hooks/usePhoneLandscape';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { UserImpersonationSelector } from './UserImpersonationSelector';
import { ImportJobsIndicator } from './ImportJobsIndicator';
// v1.1.26 — net-new chip for the v1.1.25 existing-asset offline queue.
// Renders nothing when idle; never replaces the existing offline indicators.
import OfflineExistingAssetIndicator from './OfflineExistingAssetIndicator';
// v1.3.16 — Manual Airplane Mode toggle. Renders nothing when the
// feature flag REACT_APP_ENABLE_MANUAL_AIRPLANE_MODE is off, so
// tenants that haven't opted in see the sidebar exactly as before.
import AirplaneModeToggle from './AirplaneModeToggle';
// v1.3.18 — Phase 2B queue pill: shows a "N tasks queued" chip
// next to the Focus Mode toggle when deferred AI jobs are pending,
// opens the queue drawer on click. Renders nothing when the count
// is 0 or the feature flag is off.
import FocusModeQueuePill from './FocusModeQueuePill';
import { useConnectivity, MANUAL_AIRPLANE_ENABLED } from '../lib/connectivity';
import Avatar from './Avatar';
import { FeedbackDialog } from './feedback/FeedbackDialog';
import RemovalQueueBell from './RemovalQueueBell';
import { ClipboardList, Building2, LogOut, LayoutDashboard, UserCheck, Briefcase, Menu, X, Map, MapPin, Route, BarChart3, Moon, Sun, Wrench, CalendarClock, Calendar, Users, FileCheck, FileText, ChevronDown, ChevronRight, Database, Settings, Package, Layers, Activity, MessageCircle, TrendingUp, MessageSquare, MessageSquarePlus, DollarSign, Sparkles, Wifi, WifiOff, Upload, ShoppingCart, AlertTriangle, Zap, PenTool, Brain, Search, Cpu, HardHat, LineChart, PanelLeftOpen, PanelLeftClose, BookOpen, Boxes, ShieldAlert, ShieldCheck, Radio, LayoutGrid, Command } from 'lucide-react';
import { 
  isAdmin, 
  isSmeOrAbove, 
  isSurveyorOrAbove, 
  isTechnicianOrAbove, 
  isPlannerOrAbove,
  getRoleDisplayName 
} from '../lib/permissions';
import { workOrdersApi } from '../lib/api';
import { getVisibleModules, isItemVisible } from '../lib/modules';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { features } = useFeatures();
  const { isImpersonating, targetUser, canImpersonate } = useImpersonation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsedPref, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  // v1.3.37.140 — sideways phone uses the slide-in drawer (never the icon rail);
  // close the drawer whenever the phone rotates.
  const isPhoneLandscape = usePhoneLandscape();
  const sidebarCollapsed = sidebarCollapsedPref && !isPhoneLandscape;
  useEffect(() => { setMobileMenuOpen(false); }, [isPhoneLandscape]);
  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', String(next));
      return next;
    });
  };
  const [expandedSections, setExpandedSections] = useState({
    'home': true,
    'assess': false,
    'plan': false,
    'operate': false,   // v1.3.37.61 — renamed from 'execute'
    // v1.3.37.74 — 'predict' key removed; Predict is now a child of Operate.
    'optimize': false,
    'system': false,
  });
  const sidebarRef = useRef(null);
  const sidebarScrollPos = useRef(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [activeWorkOrdersCount, setActiveWorkOrdersCount] = useState(0);
  const [feedbackNewCount, setFeedbackNewCount] = useState(0);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [networkOnline, setNetworkOnline] = useState(navigator.onLine);
  // v1.3.16 — Manual airplane mode reactive state. This is what drives
  // the header chip's colour so the toggle change is visible immediately.
  const { manualAirplane } = useConnectivity();

  // v1.2.40 — page-visit breadcrumb ring buffer. Feeds the "Report an
  // issue" dialog with the last N routes so admins can see the
  // assessor's path when triaging a report. Persisted to
  // sessionStorage so it survives dialog re-mounts.
  const feedbackBreadcrumbRef = useRef([]);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('feedback:breadcrumb');
      feedbackBreadcrumbRef.current = raw ? JSON.parse(raw) : [];
    } catch (_) {
      feedbackBreadcrumbRef.current = [];
    }
  }, []);
  useEffect(() => {
    const entry = `${location.pathname}${location.search || ''}`;
    const prev = feedbackBreadcrumbRef.current;
    if (prev[prev.length - 1] === entry) return;
    const next = [...prev, entry].slice(-5);
    feedbackBreadcrumbRef.current = next;
    try {
      sessionStorage.setItem('feedback:breadcrumb', JSON.stringify(next));
    } catch (_) {
      // ignore
    }
  }, [location.pathname, location.search]);

  // Monitor online/offline status globally
  useEffect(() => {
    const onOnline = () => setNetworkOnline(true);
    const onOffline = () => setNetworkOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // v1.3.16 — Boot-time nudge if the user's previous session left
  // manual airplane mode ON. Prevents the "why isn't anything
  // syncing?" surprise a day later. Fires exactly once per app
  // mount, and only when the feature flag is on AND the persisted
  // state is on.
  useEffect(() => {
    if (!MANUAL_AIRPLANE_ENABLED) return;
    if (!manualAirplane) return;
    // Lazy import so the login screen (which mounts before Layout)
    // doesn't drag in the toast library needlessly.
    import('sonner').then(({ toast }) => {
      toast.info(
        'Focus Mode is still ON from your last session — tap the switch in the sidebar to resume syncing.',
        { duration: 8000, id: 'boot-airplane-nudge' }
      );
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch counts for navigation badges
  const fetchCounts = useCallback(async () => {
    if (!user || !features.iwms_enabled) return;
    try {
      const [unreadData, activeData] = await Promise.all([
        workOrdersApi.getUnreadMessagesCount(),
        workOrdersApi.getActiveCount()
      ]);
      setUnreadMessagesCount(unreadData.unread_count || 0);
      setActiveWorkOrdersCount(activeData.active_count || 0);
    } catch (error) {
      console.error('Failed to fetch navigation counts:', error);
    }
  }, [user, features.iwms_enabled]);

  // v1.2.34 — poll the feedback "new" count for the admin sidebar badge.
  // Runs on the same 60 s cadence as the other nav counts. Silently 403s
  // for non-admin roles; we just leave the badge at 0 in that case.
  const fetchFeedbackCount = useCallback(async () => {
    if (!user || !['founder', 'super_admin', 'admin'].includes(user.role)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback/count-new`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setFeedbackNewCount(data.count || 0);
      }
    } catch (_) {
      // ignore — badge just stays at previous value
    }
  }, [user]);

  // Fetch counts on mount and periodically
  useEffect(() => {
    fetchCounts();
    fetchFeedbackCount();
    // Poll every 60 seconds for updates
    const interval = setInterval(() => {
      fetchCounts();
      fetchFeedbackCount();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchCounts, fetchFeedbackCount]);

  // Refetch when navigating away from work orders or messaging
  useEffect(() => {
    if (!location.pathname.startsWith('/work-orders') && !location.pathname.startsWith('/messaging')) {
      fetchCounts();
    }
  }, [location.pathname, fetchCounts]);

  // Refresh counts when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchCounts();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchCounts]);

  // Auto-expand the sidebar section that contains the current route.
  // All other sections collapse for a cleaner look. User's manual toggles
  // (via toggleSection) still work because this effect only fires when
  // the URL path changes.
  useEffect(() => {
    // path prefix → section key (matches the navigation arrays below).
    // Warranties + Vendors live in Portfolio for everyone (v1.1.61) — the
    // earlier launchpad-aware re-mapping was confusing users who expected
    // those entries in their native Portfolio home regardless of role.
    // v1.3.37.61 — BELM (Built Environment Lifecycle Management) alignment:
    // "Execute" section renamed to "Operate", and the four M&O artifacts
    // (Procedures / Job Plans / Work Packages / Blackouts) migrated from
    // Plan → Operate so all day-to-day critical-environment tools live
    // under one roof.  CAB Inbox + IoT Sensors added under Operate too.
    const ROUTE_TO_SECTION = [
      // Assess
      ['/assessments',         'assess'],
      ['/building-inspections','assess'],
      ['/triage',              'assess'],
      ['/spatial',             'assess'],
      // Plan (capital / procurement / planning-map only)
      ['/capital-projects',    'plan'],
      ['/procurement-planner', 'plan'],
      ['/planning-map',        'plan'],
      // Operate (formerly Execute) — every M&O artifact lives here
      ['/operate',             'operate'],
      ['/work-orders',         'operate'],
      ['/pm-schedules',        'operate'],
      ['/scheduler',           'operate'],
      ['/calendar',            'operate'],
      ['/technician',          'operate'],
      ['/messaging',           'operate'],
      ['/operations-map',      'operate'],
      ['/iwms-admin',          'operate'],
      // v1.3.37.63 — DC / critical-environment tools moved under Critical
      // Operations hub, still nested inside the Operate sidebar section.
      ['/critical-ops',        'operate'],
      ['/procedures',          'operate'],
      ['/job-plans',           'operate'],
      ['/work-packages',       'operate'],
      ['/blackouts',           'operate'],
      ['/cab-inbox',           'operate'],
      ['/cab/requests',        'operate'],
      // v1.3.37.63 — Predict hub: real-time analytics (IoT + energy).
      // v1.3.37.74 — Section moved from 'predict' to 'operate' since
      // Predict is now a child of Operate in the sidebar.
      ['/predict',             'operate'],
      ['/iot-sensors',         'operate'],
      // Optimize
      ['/dashboard',           'optimize'],
      ['/ai-dashboard',        'optimize'],
      ['/insights',            'optimize'],
      ['/finance-hub',         'optimize'],
      // (Sites & Zones — formerly /gis-map — now lives at /operations-map?view=sites)
      ['/gis-map',             'operate'],
      // Portfolio
      ['/clients',             'portfolio'],
      ['/facilities',          'portfolio'],
      ['/zones',               'portfolio'],
      ['/iwms-assets',         'portfolio'],
      ['/warranties',          'portfolio'],
      ['/leases',              'portfolio'],
      ['/vendors',             'portfolio'],
      // System
      ['/organization',        'system'],
      ['/pricing-ingestion',   'system'],
      ['/audit-log',           'system'],
    ];
    const match = ROUTE_TO_SECTION.find(([prefix]) => location.pathname.startsWith(prefix));
    const activeKey = match ? match[1] : null;
    setExpandedSections({
      home: true,
      assess: activeKey === 'assess',
      plan: activeKey === 'plan',
      operate: activeKey === 'operate',   // v1.3.37.61 — renamed from 'execute'
      predict: activeKey === 'predict',   // v1.3.37.63
      optimize: activeKey === 'optimize',
      portfolio: activeKey === 'portfolio',
      system: activeKey === 'system',
    });
  }, [location.pathname, user, isImpersonating, targetUser, features]);

  // Listen for work order changes (delete, create, status change)
  useEffect(() => {
    const handleWorkOrderChange = () => {
      fetchCounts();
    };
    
    window.addEventListener('workorder-changed', handleWorkOrderChange);
    return () => window.removeEventListener('workorder-changed', handleWorkOrderChange);
  }, [fetchCounts]);

  // Preserve sidebar scroll position across route changes
  useEffect(() => {
    // Use requestAnimationFrame to restore after React finishes rendering
    const restoreScroll = () => {
      if (sidebarRef.current && sidebarScrollPos.current > 0) {
        sidebarRef.current.scrollTop = sidebarScrollPos.current;
      }
    };
    
    // Small delay to ensure DOM is updated
    requestAnimationFrame(() => {
      requestAnimationFrame(restoreScroll);
    });
  }, [location.pathname]);

  const handleSidebarScroll = () => {
    if (sidebarRef.current) {
      sidebarScrollPos.current = sidebarRef.current.scrollTop;
    }
  };

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    setMobileMenuOpen(false);
    logout();
    navigate('/login');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Build navigation structure — Assess / Plan / Operate / Optimize / System
  const navigationSections = [];
  // When impersonating, use the target user's role and nav config
  const effectiveUser = isImpersonating && targetUser ? {
    ...user,
    role: targetUser.role,
    visible_modules: targetUser.visible_modules || [],
    hidden_items: targetUser.hidden_items || []
  } : user;
  const userRole = effectiveUser?.role;
  const visibleModules = getVisibleModules(effectiveUser, features);
  const hasModule = (key) => visibleModules.includes(key);
  const filterItems = (items) => items.filter(item => isItemVisible(effectiveUser, item.href));

  // HOME - removed; Launchpad moved to System menu

  // ── ASSESS ──
  if (isSurveyorOrAbove(userRole) && (hasModule('assessments') || hasModule('portfolio'))) {
    const assessItems = filterItems([
      ...(features.fca_enabled !== false ? [{ name: 'Assets', href: '/assessments', icon: ClipboardList }] : []),
      ...(features.building_inspections_enabled ? [{ name: 'Buildings', href: '/building-inspections', icon: FileCheck }] : []),
      // v1.3.0.4 — Damage Assessment and Drawings Assessment are
      // strategically paused. Visibility is controlled purely by the
      // existing per-client feature flags:
      //   * features.damage_triage_enabled
      //   * features.drawing_intel_enabled
      // The backend (routes/features.py::get_my_features) already
      // returns `true` for both flags whenever the user is
      // super_admin+ (so founders / super_admins always see these
      // items for development / QA), and `false` by default for
      // every other role unless a client admin explicitly opts the
      // client in via clients.features.{flag} = true. To grant a
      // customer access without role promotion, flip that flag on
      // that customer's client record — no code change needed.
      //
      // Note: `drawing_intel_enabled` used to default `!== false`
      // (i.e. visible when unset) — that meant it showed for every
      // role until an admin explicitly turned it off. Now it's a
      // truthy check like every other feature flag, so the default
      // is OFF and admins opt-in explicitly (matching damage_triage).
      ...(features.damage_triage_enabled ? [{ name: 'Damage', href: '/triage', icon: AlertTriangle }] : []),
      ...(features.drawing_intel_enabled ? [{ name: 'Drawings', href: '/spatial/dashboard', icon: Layers }] : []),
    ]);
    if (assessItems.length > 0) {
      navigationSections.push({ name: 'Assess', key: 'assess', icon: Search, items: assessItems });
    }
  }

  // ── PLAN ──
  // v1.3.37.61 — Procedures / Job Plans / Work Packages / Blackouts
  // migrated to Operate (BELM alignment).  Plan retains only the
  // strategic / capital-planning artifacts.
  if (isSurveyorOrAbove(userRole) && (hasModule('assessments') || hasModule('planning'))) {
    const planItems = filterItems([
      ...(features.project_management_enabled ? [{ name: 'Capital Projects', href: '/capital-projects', icon: Briefcase }] : []),
      ...(features.procurement_planner_enabled ? [{ name: 'Procurement', href: '/procurement-planner', icon: ShoppingCart }] : []),
      ...(features.fca_enabled !== false ? [{ name: 'Planning Map', href: '/planning-map', icon: Map }] : []),
    ]);
    if (planItems.length > 0) {
      navigationSections.push({ name: 'Plan', key: 'plan', icon: Cpu, items: planItems });
    }
  }

  // ── OPERATE ──  (formerly "Execute", renamed v1.3.37.61 for BELM alignment)
  // The M&O home.  Every day-to-day critical-environment tool lives here:
  // Work Management (WOs / PMs / Scheduler), procedure library + runner,
  // job plans, work packages, blackouts, CAB approvals.
  //
  // v1.3.37.63: Restructured into TWO hubs at the sidebar level:
  //   * "Operate" (/operate) — WMS-style dashboard + Work Orders / PM
  //     Schedules / Scheduler / Calendar / Operations Map tabs.
  //   * "Critical Operations" (/critical-ops) — DC-flavoured hub for
  //     Procedures / Job Plans / Work Packages / CAB Inbox, nested under
  //     the Operate sidebar section and appearing only when at least one
  //     DC feature flag is on.
  // Per-tool direct routes are still kept in the sidebar (Work Orders,
  // PM Schedules, Scheduler, Calendar, My Schedule, Messaging,
  // Operations Map) so muscle memory + notification deep-links keep
  // working exactly as before.
  //
  // IoT Sensors moved out to the new "Predict" section below.
  if (features.iwms_enabled && isTechnicianOrAbove(userRole) && hasModule('work_management')) {
    const anyDcFlag = !!(features.procedures_module_enabled
      || features.job_plans_enabled
      || features.cab_approval_enabled
      || features.blackout_windows_enabled
      // v1.3.37.113 — Phase 4 Ops flags also open the hub so
      // the new Shifts / Ops Log / Incidents / Rounds / Handover
      // tabs get a nav entry as soon as any of them is on.
      || features.shifts_enabled
      || features.ops_log_enabled
      || features.incidents_enabled
      || features.rounds_enabled
      || features.handover_enabled
      // v1.3.37.116 — Phase 3 operator boards (Maintenance Modes,
      // LOTO, Work Permits) also open the hub as first-class tabs.
      || features.maintenance_mode_enabled
      || features.loto_permits_enabled);
    // v1.3.37.74 — Predict lives under Operate now (as a child), not
    // as its own top-level section.  Keeps every "day-to-day M&O
    // surface" collapsed under one header so the sidebar reads
    // Assess → Plan → Operate → Optimize → Portfolio → System.
    const canSeePredict = features.iot_sensors_enabled && isPlannerOrAbove(userRole);
    const operateItems = filterItems([
      // v1.3.37.63.1 — After the tab strip took over WOs / PM Schedules /
      // Scheduler / Calendar / Operations Map (they're all first-class tabs
      // inside the Operate hub now), the standalone sidebar entries became
      // duplicative.  Kept: hub, My Schedule (personal shortcut), Messaging
      // (unread badge is worth the click), Critical Operations (nested hub).
      // v1.3.37.63.2 — Renamed the hub entry from "Operate" to "Work
      // Management" and switched its icon from LayoutGrid to Command so
      // it stops colliding visually with the Operate & Predict LayoutGrid
      // tiles.  The parent sidebar SECTION HEADER stays labelled "Operate"
      // -- only the child item that navigates to /operate changes.
      { name: 'Work Management', href: '/operate', icon: Command },
      { name: 'My Schedule', href: '/technician', icon: ClipboardList },
      { name: 'Messaging', href: '/messaging', icon: MessageSquare, badge: unreadMessagesCount },
      // v1.3.37.63 — Critical Operations hub, nested under Operate.  Only
      // shows for surveyor+ AND when at least one DC feature flag is on.
      ...(anyDcFlag && isSurveyorOrAbove(userRole)
          ? [{ name: 'Critical Operations', href: '/critical-ops', icon: ShieldCheck }] : []),
      // v1.3.37.74 — Predict moved from its own top-level section
      // into Operate as a child so all day-to-day M&O surfaces sit
      // under one header.  Same feature gate as before
      // (iot_sensors_enabled + planner+).
      ...(canSeePredict ? [{ name: 'Predict', href: '/predict', icon: Radio }] : []),
    ]);
    if (operateItems.length > 0) {
      navigationSections.push({ name: 'Operate', key: 'operate', icon: HardHat, items: operateItems });
    }
  }

  // ── PREDICT ──  (v1.3.37.63 → flattened v1.3.37.72 → moved under
  //                Operate v1.3.37.74)
  // The Predict entry now lives as a child inside the Operate
  // section above.  This block is intentionally empty — kept as an
  // anchor comment so grep-for-PREDICT still lands somewhere
  // meaningful, and so the isPlannerOrAbove + iot_sensors_enabled
  // gate remains visible in one grep.

  // ── OPTIMIZE ──
  // Note: GIS Dashboard used to live here; it's been folded into Operations
  // Map (Operate) as the "Sites & Zones" tab so all spatial workflows live
  // in one page.
  if (isAdmin(userRole) && (hasModule('planning') || hasModule('portfolio'))) {
    const optimizeItems = filterItems([
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      ...(features.fca_enabled !== false ? [
        { name: 'Ask FMiQ-One', href: '/ai-dashboard', icon: Sparkles },
        { name: 'Insights Builder', href: '/insights', icon: BarChart3 },
        { name: 'Finance & Reporting', href: '/finance-hub', icon: BarChart3 },
      ] : []),
      { name: 'Programs', href: '/programs', icon: Boxes },
    ]);
    if (optimizeItems.length > 0) {
      navigationSections.push({ name: 'Optimize', key: 'optimize', icon: LineChart, items: optimizeItems });
    }
  }

  // ── PORTFOLIO ──
  // Operational data surfaces (clients, facilities, zones, assets, warranties,
  // leases, vendors). Split out from System so day-to-day admin doesn't have
  // to wade through infrastructure-level admin entries.
  //
  // NOTE (v1.1.61): Vendors and Warranties used to be hidden from the sidebar
  // when the user had access to the IWMS Launchpad (/iwms-admin) because
  // those concepts also live as tabs inside the launchpad. Per product owner
  // request, the de-duplication was confusing — users expected Vendors and
  // Warranties as first-class sidebar entries regardless of launchpad
  // availability. They now always render here when iwms_enabled is on.
  if (isAdmin(userRole) && (hasModule('admin') || hasModule('portfolio'))) {
    const portfolioItems = filterItems([
      { name: 'Clients', href: '/clients', icon: Briefcase },
      { name: 'Facilities', href: '/facilities', icon: Building2 },
      { name: 'Zones', href: '/zones', icon: MapPin },
      ...(features.iwms_enabled ? [
        { name: 'Asset Inventory', href: '/iwms-assets', icon: Package },
        { name: 'Warranties', href: '/warranties', icon: FileCheck },
        { name: 'Leases', href: '/leases', icon: FileText },
        { name: 'Vendors', href: '/vendors', icon: Users },
      ] : []),
    ]);
    if (portfolioItems.length > 0) {
      navigationSections.push({ name: 'Portfolio', key: 'portfolio', icon: Layers, items: portfolioItems });
    }
  }

  // ── SYSTEM (Admin) ──
  if (isAdmin(userRole) && (hasModule('admin') || hasModule('portfolio'))) {
    // v1.3.0.6 — Removed the redundant "Super System Admin" sidebar link.
    // Super_admin+ users flip modes via the persistent toggle at the top
    // of /organization; the dedicated sidebar entry created duplication
    // and clutter without any workflow benefit.
    const systemItems = filterItems([
      { name: 'System Admin', href: '/organization', icon: Database },
      // v1.3.31.8 — API Keys moved off the sidebar into a tab inside
      // OrganizationPage → Super System Admin → "API Keys". The route
      // /admin/api-keys still resolves for backwards compat with any
      // existing bookmarks; there just isn't a sidebar link for it.
      { name: 'Feedback', href: '/admin/feedback', icon: MessageSquarePlus, badge: feedbackNewCount },
      // v1.3.35.81 — Mobile Asset Tracking admin surfaces.  Both pages
      // pre-existed (v.76 + v.78) but were only reachable by URL until
      // this release added the sidebar entries.
      { name: 'Trackers', href: '/admin/trackers', icon: Cpu },
      { name: 'Geofences', href: '/admin/geofences', icon: Map },
    ]);
    if (systemItems.length > 0) {
      navigationSections.push({ name: 'System', key: 'system', icon: Settings, items: systemItems });
    }
  }

  const handleNavClick = (e) => {
    // Capture current sidebar scroll position before navigation
    const currentScroll = sidebarRef.current?.scrollTop || 0;
    sidebarScrollPos.current = currentScroll;
    
    // Only close menu on mobile (don't scroll main content)
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex transition-colors duration-200" data-testid="app-layout">
      {/* Mobile Header */}
      <div className="md:hidden phoneland:flex fixed top-0 left-0 right-0 bg-slate-900 text-white z-40 flex items-center justify-between px-4 py-3 phoneland:py-1" data-testid="mobile-header">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-md hover:bg-slate-800 transition-colors"
          data-testid="mobile-menu-button"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <div className="flex items-center gap-2">
          {/* FMiQ-One Logo for mobile header — clickable */}
          <Link to="/" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-logo-link">
            <img 
              src="/images/fmiq-one-logo.png" 
              alt="FMiQ-One" 
              className="h-10 phoneland:h-7" 
            />
          </Link>
          {/* Mobile offline indicator */}
          {!networkOnline && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium"
                  data-testid="mobile-offline-indicator">
              <WifiOff size={12} />
              Offline
            </span>
          )}
          {/* v1.1.26 — pending-sync chip for the new existing-asset offline
              queue. Renders nothing when the queue is empty + idle, so it
              never adds visual noise. Lives BESIDE the existing offline
              indicator above; doesn't replace it. */}
          <OfflineExistingAssetIndicator />
        </div>
        {/* Dark mode toggle for mobile header */}
        <button
          onClick={toggleDarkMode}
          className="p-2 rounded-md hover:bg-slate-800 transition-colors"
          data-testid="dark-mode-toggle-mobile"
          title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {darkMode ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-slate-300" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden phoneland:block fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop always visible (rail or full width), Mobile slides in */}
      <aside 
        ref={sidebarRef}
        onScroll={handleSidebarScroll}
        className={`
          fixed inset-y-0 left-0 z-50
          ${sidebarCollapsed ? 'md:w-16' : 'md:w-60'}
          w-60 text-white
          transform transition-all duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0 phoneland:translate-x-0' : '-translate-x-full phoneland:-translate-x-full'}
          md:translate-x-0
          overflow-y-auto overscroll-contain
          md:sticky md:top-0 md:h-screen
          phoneland:fixed phoneland:w-60
        `}
        style={{ 
          WebkitOverflowScrolling: 'touch', 
          scrollBehavior: 'auto',
          backgroundColor: '#0F172A' /* FMiQ Deep Navy */
        }}
        data-testid="sidebar"
      >
        <div className="flex flex-col min-h-full">
          {/* Close button for mobile + collapse/expand toggle for desktop */}
          <div className={`flex p-2 sticky top-0 z-10 ${sidebarCollapsed ? 'md:justify-center' : 'justify-end'}`} style={{ backgroundColor: '#0F172A' }}>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="md:hidden phoneland:flex p-2 rounded-md hover:bg-slate-800 transition-colors"
            >
              <X size={24} />
            </button>
            <button
              onClick={toggleSidebar}
              className="hidden md:flex phoneland:hidden p-2 rounded-md hover:bg-slate-800 transition-colors text-slate-300 hover:text-white"
              data-testid="sidebar-collapse-btn"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
          </div>

          <div className={`border-b border-slate-700/50 flex-shrink-0 ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
            {/* FMiQ Logo Container — clickable, routes to preferred Launchpad */}
            <Link
              to="/"
              onClick={handleNavClick}
              className="relative group flex justify-center"
              data-testid="sidebar-logo-link"
              title="Go to Launchpad"
            >
              {/* Animated glow behind logo - Using brand Primary Blue */}
              <div className="absolute inset-0 bg-gradient-to-b from-blue-500/20 via-blue-400/10 to-amber-500/10 rounded-xl blur-xl opacity-50 group-hover:opacity-80 transition-all duration-500 animate-pulse" style={{animationDuration: '3s'}} />
              
              {/* PNG Logo */}
              <img 
                src={sidebarCollapsed ? "/images/fmiq-icon.png" : "/images/fmiq-one-logo.png"}
                alt="FMiQ-One"
                onError={(e) => { e.currentTarget.src = "/images/fmiq-one-logo.png"; }}
                className={`relative mx-auto group-hover:scale-105 transition-transform duration-300 ${sidebarCollapsed ? 'w-10 h-10' : 'w-full max-w-[140px]'}`}
              />
            </Link>
          </div>

          <nav className={`space-y-2 flex-shrink-0 ${sidebarCollapsed ? 'p-2' : 'p-4'}`} data-testid="main-navigation">
            {navigationSections.map((section) => {
              // Direct link section (no sub-items, just a single link)
              const isDirectLink = section.href && (!section.items || section.items.length === 0);
              
              if (isDirectLink) {
                const Icon = section.icon;
                const isActive = location.pathname === section.href || location.pathname.startsWith(section.href);
                return (
                  <div key={section.key} className="mb-2">
                    <Link
                      to={section.href}
                      onClick={handleNavClick}
                      data-testid={`nav-link-${section.name.toLowerCase().replace(/ /g, '-')}`}
                      title={sidebarCollapsed ? section.name : ''}
                      className={`flex items-center gap-3 rounded-md transition-all duration-200 ${
                        sidebarCollapsed ? 'md:justify-center md:px-2 md:py-2.5 px-4 py-2.5' : 'px-4 py-2.5'
                      } ${
                        isActive
                          ? 'bg-sky-500 text-white shadow-sm'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      {Icon && <Icon size={18} />}
                      <span className={`font-medium text-sm ${sidebarCollapsed ? 'md:hidden' : ''}`}>{section.name}</span>
                    </Link>
                  </div>
                );
              }
              
              // Expandable section with sub-items
              return (
                <div key={section.key} className="mb-2">
                  {/* Section Header - hidden when collapsed; otherwise clickable to collapse/expand */}
                  {!section.alwaysOpen && !sidebarCollapsed && (
                    <button
                      onClick={() => toggleSection(section.key)}
                      className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-300"
                    >
                      <span>{section.name}</span>
                      {expandedSections[section.key] ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  {/* Visual divider between sections in rail mode */}
                  {!section.alwaysOpen && sidebarCollapsed && (
                    <div className="hidden md:block border-t border-slate-700/50 my-2" />
                  )}
                  
                  {/* Section Items — always shown in rail mode */}
                  {(section.alwaysOpen || expandedSections[section.key] || sidebarCollapsed) && (
                    <div className="space-y-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.href;
                        const hasBadge = item.badge && item.badge > 0;
                        const isNewFeature = item.isNew;
                        
                        return (
                          <div key={item.name} className="relative">
                            <Link
                              to={item.href}
                              onClick={handleNavClick}
                              onMouseDown={(e) => {
                                if (sidebarRef.current) {
                                  sidebarScrollPos.current = sidebarRef.current.scrollTop;
                                }
                              }}
                              tabIndex={-1}
                              data-testid={`nav-link-${item.name.toLowerCase().replace(/ /g, '-')}`}
                              title={sidebarCollapsed ? item.name : ''}
                              className={`flex items-center gap-3 rounded-md transition-all duration-200 relative ${
                                sidebarCollapsed ? 'md:justify-center md:px-2 md:py-2.5 px-4 py-2.5' : 'px-4 py-2.5'
                              } ${
                                isActive
                                  ? 'bg-sky-500 text-white shadow-sm'
                                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                              }`}
                            >
                              <Icon size={18} />
                              <span className={`font-medium text-sm flex-1 ${sidebarCollapsed ? 'md:hidden' : ''}`}>{item.name}</span>
                              {isNewFeature && !sidebarCollapsed && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-teal-500 text-white">
                                  NEW
                                </span>
                              )}
                              {hasBadge && !sidebarCollapsed && (
                                <span
                                  className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full bg-red-500 text-white"
                                  data-testid={`nav-badge-${item.name.toLowerCase().replace(' ', '-')}`}
                                  title={item.href === '/work-orders' ? 'Open work orders' : item.href === '/messaging' ? 'Unread messages' : ''}
                                >
                                  {item.badge > 99 ? '99+' : item.badge}
                                </span>
                              )}
                              {/* Compact badge dot in rail mode */}
                              {hasBadge && sidebarCollapsed && (
                                <span className="hidden md:block absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full ring-2 ring-[#0F172A]" />
                              )}
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Spacer to push footer to bottom on tall screens */}
          <div className="flex-grow" />

          <div className={`border-t border-slate-800 flex-shrink-0 ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
            {/* User name + role with avatar — hidden in rail mode (avatar shows alone) */}
            {sidebarCollapsed ? (
              <div className="flex flex-col items-center gap-1 mb-2">
                <Avatar
                  src={user?.avatar_url}
                  name={user?.full_name || user?.email}
                  size="md"
                  testId="sidebar-user-avatar-rail"
                  title={user?.full_name}
                />
                <RemovalQueueBell user={user} compact />
              </div>
            ) : (
              <div className="mb-2 flex items-center gap-2.5" data-testid="user-info">
                <Avatar
                  src={user?.avatar_url}
                  name={user?.full_name || user?.email}
                  size="lg"
                  testId="sidebar-user-avatar"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate">{user?.full_name}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wider truncate">{getRoleDisplayName(user?.role)}</p>
                </div>
                <RemovalQueueBell user={user} />
              </div>
            )}

            {/* Connection status */}
            {!networkOnline ? (
              <div className={`mb-2 px-2 py-1 rounded bg-amber-500/20 border border-amber-500/30 flex items-center ${sidebarCollapsed ? 'md:justify-center' : 'gap-1.5'}`}
                   data-testid="global-offline-indicator"
                   title={sidebarCollapsed ? 'Offline' : ''}>
                <WifiOff size={12} className="text-amber-400 flex-shrink-0" />
                <span className={`text-[11px] text-amber-300 font-medium ${sidebarCollapsed ? 'md:hidden' : ''}`}>Offline</span>
              </div>
            ) : manualAirplane ? (
              /* v1.3.16 — Manual airplane mode chip. Distinct sky-blue
                 to differentiate from true offline (amber) and normal
                 online (emerald). Only shown when the feature flag is
                 on AND the user has flipped the toggle. */
              <div className={`mb-2 px-2 py-1 rounded bg-sky-500/20 border border-sky-400/30 flex items-center ${sidebarCollapsed ? 'md:justify-center' : 'gap-1.5'}`}
                   data-testid="global-airplane-indicator"
                   title={sidebarCollapsed ? 'Focus Mode — queueing locally' : ''}>
                <WifiOff size={12} className="text-sky-300 flex-shrink-0" />
                <span className={`text-[11px] text-sky-200 font-medium ${sidebarCollapsed ? 'md:hidden' : ''}`}>Focus Mode</span>
              </div>
            ) : (
              <div className={`mb-2 px-2 py-1 flex items-center ${sidebarCollapsed ? 'md:justify-center' : 'gap-1.5'}`}
                   data-testid="global-online-indicator"
                   title={sidebarCollapsed ? 'Connected' : ''}>
                <Wifi size={12} className="text-emerald-400 flex-shrink-0" />
                <span className={`text-[11px] text-slate-500 ${sidebarCollapsed ? 'md:hidden' : ''}`}>Connected</span>
              </div>
            )}

            {/* v1.3.16 — Manual airplane mode toggle. Renders nothing
                when REACT_APP_ENABLE_MANUAL_AIRPLANE_MODE is off. */}
            <AirplaneModeToggle collapsed={sidebarCollapsed} />

            {/* v1.3.18 — Deferred AI jobs queue pill. Renders nothing
                when there are no pending jobs or the flag is off. */}
            <FocusModeQueuePill collapsed={sidebarCollapsed} />


            {/* User Settings — single button (similar to top-level nav links). All
                sub-functionality (security, theme, view-as, logout, profile)
                lives on the /settings page. */}
            {sidebarCollapsed ? (
              <>
                <Link to="/settings" onClick={handleNavClick} title="User Settings">
                  <Button variant="ghost" size="sm"
                    className="w-full justify-center text-slate-300 hover:text-white hover:bg-slate-800"
                    data-testid="user-settings-link">
                    <Settings size={16} />
                  </Button>
                </Link>
                {/* v1.2.40 — Report an issue (renamed "Send Feedback" in
                    v1.3.0.6) moved from a floating pill to a permanent
                    sidebar entry directly under User Settings. */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFeedbackDialogOpen(true)}
                  title="Send Feedback"
                  className="w-full justify-center text-slate-300 hover:text-white hover:bg-slate-800"
                  data-testid="sidebar-report-issue-btn"
                >
                  <MessageSquarePlus size={16} />
                </Button>
              </>
            ) : (
              <>
                <Link to="/settings" onClick={handleNavClick}>
                  <Button variant="ghost" size="sm"
                    className={`w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800 ${
                      location.pathname === '/settings' ? 'bg-slate-800 text-white' : ''
                    }`}
                    data-testid="user-settings-link">
                    <Settings size={16} className="mr-2 text-teal-400" />
                    User Settings
                  </Button>
                </Link>

                {/* v1.2.40 — Report an issue (renamed "Send Feedback" in
                    v1.3.0.6 so it covers bugs, suggestions AND questions
                    without the negative framing). Uses the shared
                    FeedbackDialog (opens controlled by feedbackDialogOpen
                    state above). */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFeedbackDialogOpen(true)}
                  className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
                  data-testid="sidebar-report-issue-btn"
                >
                  <MessageSquarePlus size={16} className="mr-2 text-teal-400" />
                  Send Feedback
                </Button>

                <div className="mt-2 flex justify-start">
                  <ImportJobsIndicator />
                </div>

                <div className="mt-3 pt-3 border-t border-slate-800 flex justify-center">
                  <img src="/sba-badge.png" alt="Service-Disabled Veteran-Owned Certified" className="w-16 opacity-70 hover:opacity-100 transition-opacity" />
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content - add top padding when impersonating.
          v1.1.71 — Dropped `overflow-auto` from <main>. It was forming a
          `position: sticky` containing block for descendant pages even
          though <main> itself never scrolled (the document/window
          scrolls because the outer flex container is min-h-screen, not
          h-screen). With overflow-auto in place, page-level sticky bars
          (filter/search) were anchoring to <main>'s top and scrolling
          OUT of the viewport along with <main>. Removing overflow-auto
          lets sticky bars anchor to the viewport. */}
      <main 
        className={`flex-1 min-w-0 bg-slate-50 dark:bg-slate-900 transition-colors duration-200 ${
          isImpersonating ? 'pt-24 md:pt-10 phoneland:pt-20' : 'pt-14 md:pt-0 phoneland:pt-10'
        }`}
        data-testid="main-content"
      >
        {children}
      </main>

      {/* v1.2.40 — Feedback dialog is now triggered by the sidebar
          entry (Report an issue) directly under User Settings. The
          previous floating persistent pill has been retired. */}
      <FeedbackDialog
        open={feedbackDialogOpen}
        onOpenChange={setFeedbackDialogOpen}
        pageUrl={`${location.pathname}${location.search || ''}`}
        breadcrumb={feedbackBreadcrumbRef.current}
      />
    </div>
  );
};

export default Layout;
